<template>
    <div class="a_list">
        <div class="info">
            <ul>
                <li>
                    <div class="info_lbl">{{$t('SurveyEditorContent_surveyTitle')}}</div>
                    <div class="info_txt">
                                    <TextArea
                                            ref="title"
                                            id="title"
                                            cols="50"
                                            rows="1"
                                            :maxlength="40"
                                            :placeholder="$t('SurveyEditorContent_inputSurveyTitle')"
                                            :show-count="true"
                                            v-model="title"
                                            :resize="true"
                                            :one-line="true"
                                            wrapper-class="tit_survey"
                                    />
                    </div>
                </li>
                <li>
                    <div class="info_lbl">{{$t('SurveyEditorContent_surveyDesc')}}</div>
                    <div class="info_txt">
                                    <TextArea
                                            ref="surveyDesc"
                                            id="surveyDesc"
                                            cols="50"
                                            rows="5"
                                            :maxlength="2000"
                                            :placeholder="$t('SurveyEditorContent_inputSurveyDesc')"
                                            v-model="surveyDesc"
                                            :resize="true"
                                            :max-height="182"
                                    />
                    </div>
                </li>

                <li>
                    <div class="info_lbl">{{$t('SurveyEditorContent_imageAttach')}}</div>
                    <div class="info_txt">
                        <ImageUpload
                                v-slot="{openFileDialog}"
                                v-on:requestUpload="uploadImage"
                                :multiple="true"
                                :now-count="images.length"
                                :max-count="maxImageCount">
                            <button type="button" class="btn btn_attach" @click.prevent="openFileDialog">
                                <span class="sp ico_plus3"></span>{{$t('SurveyEditorContent_attachImage')}}
                            </button>
                        </ImageUpload>

                        <!-- [D] 첨부 이미지 -->
                        <ul class="lst_include_file" v-if="showImages">
                            <li v-for="image in images" class="bx_include_file">
                                <div class="image">
                                    <img :src="image.url" width="580" alt="">
                                    <button type="button" class="btn_delete"
                                            v-on:click.prevent="deleteImage({imageKey: image.imageKey})">
                                        <span class="sp">{{$t('SurveyEditorContent_deleteImage')}}</span>
                                    </button>
                                </div>
                            </li>
                        </ul>
                        <span class="copy"><span :class="{txt_color: showImages}">{{ images.length }}</span>/{{$t('common_count', {count: maxImageCount})}}, {{$t('common_maximumSizePerImage')}}</span>
                    </div>
                </li>

                <li>
                    <div class="info_lbl">{{$t('SurveyEditorContent_documentAttach')}}</div>
                    <div class="info_txt">
                        <DocumentUpload v-slot="{openFileDialog}"
                                        :multiple="true"
                                        v-on:requestUpload="uploadDocument"
                                        :now-count="documents.length"
                                        :max-count="maxDocumentCount">
                            <button type="button" class="btn btn_attach" @click.prevent="openFileDialog">
                                <span class="sp ico_plus3"></span>{{$t('SurveyEditorContent_attachDocument')}}
                            </button>
                        </DocumentUpload>

                        <!-- [D] 첨부 파일 -->
                        <ul class="lst_include_file no_margin" v-if="showDocuments">
                            <template v-for="document in documents">
                                <Document
                                        :id="document.documentKey"
                                        :upload-state="document.uploadState"
                                        :file-name="document.fileName"
                                        :file-size="document.fileSize"
                                        :percent="document.uploadPercent"
                                        :document-no="document.documentNo"
                                        :token-key="document.tokenKey"
                                        v-on:downloadTempDocument="downloadTempDocument"
                                        v-on:deleteDocument="deleteDocument"
                                        v-on:retry="retryDocument"
                                />
                            </template>
                        </ul>

                        <form ref="downloadForm" :action="downloadForm.action" :method="downloadForm.method"
                              target="_download_frame"
                              enctype="application/x-www-form-urlencoded"></form>
                        <iframe width="0" height="0" style="width:0;height:0;border:0" name="_download_frame"
                                tabindex="-1"></iframe>

                        <span class="copy"><span :class="{txt_color: showDocuments}">{{ documents.length }}</span>/{{$t('common_count', {count: maxDocumentCount})}} {{$t('SurveyEditorContent_documentAttachDesc')}}</span>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import TextArea from '@iamteacherpc/components/common/TextArea.vue';
import DocumentUpload from '@iamteacherpc/components/common/DocumentUpload.vue';
import ImageUpload from '@iamteacherpc/components/common/ImageUpload.vue';
import Document from '@iamteacherpc/components/common/Document.vue';
import {mapFields} from 'vuex-map-fields';
import {DELETE_IMAGE, NAMESPACE, UPLOAD_IMAGE} from '@common/store/modules/survey/editor.js';
import editorImageDocument from '@iamteacherpc/mixins/editorImageDocument.js';
import {REQUEST_STATE} from '@iamteacherpc/consts/request.js';
import {DELETE_DOCUMENT, RETRY_DOCUMENT, UPLOAD_DOCUMENT} from '@common/store/modules/common/editor/document.js';
import {createNamespacedHelpers} from 'vuex';
import {MAX_DOCUMENT_DEFAULT_COUNT} from '@iamteacherpc/consts/document.js';
import {MAX_IMAGE_DEFAULT_COUNT} from '@iamteacherpc/consts/image.js';

const {mapActions} = createNamespacedHelpers(NAMESPACE);

export default {
    name: 'SurveyEditorContent',
    components: {Document, ImageUpload, DocumentUpload, TextArea},

    mixins: [
        editorImageDocument,
    ],

        data() {
            return {
                maxImageCount: MAX_IMAGE_DEFAULT_COUNT,
                maxDocumentCount: MAX_DOCUMENT_DEFAULT_COUNT,

                downloadForm: {
                    action: '',
                    method: '',
                },
            };
        },

        computed: {
            ...mapFields(NAMESPACE, [
                'form.title',
                'form.surveyDesc',
                'form.images',
                'form.documents',
            ]),

            showImages() {
                return this.images.some(image => image.uploadState === REQUEST_STATE.SUCCESS);
            },

            showDocuments() {
                return this.documents.length > 0;
            },
        },

        methods: {
            ...mapActions({
                storeUploadImage: UPLOAD_IMAGE,
                storeDeleteImage: DELETE_IMAGE,
                storeUploadDocument: UPLOAD_DOCUMENT,
                storeDeleteDocument: DELETE_DOCUMENT,
                storeRetryDocument: RETRY_DOCUMENT,
            }),

            downloadTempDocument({action, method}) {
                this.downloadForm.action = action;
                this.downloadForm.method = method;
                this.$nextTick(() => this.$refs.downloadForm.submit());
            },
        }
    }
</script>

<style scoped>

</style>
